import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
// remove webpack loader for below when rolling out own
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import PropTypes from 'prop-types';
import React from 'react';

import { TertiaryHeading } from 'components/Headings';
import Modal from 'components/Modal';
import Paragraph from 'components/Paragraph';
import { ENVIRONMENTS } from 'constants/environments';
import ProductModel from 'models/Product';
import VariantModel from 'models/Variant';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';
import Settings from 'utils/Settings';

import { MAPBOX_TOKENS } from './constants';
import ProductInfo from './ProductInfo';
import StoreInfo from './StoreInfo';

const StoreInventoryModal = ({
  isOpen,
  closeModal,
  product,
  variant,
  sortShops,
  setPreferredStore,
  preferredStore,
  sortedStores,
  defaultSortedList,
  storeList,
  storeSelected,
  storeSaved,
  searchCompleted,
}) => {
  let searchInitialized = false;

  const mapboxToken =
    Settings.environment === ENVIRONMENTS.STAGING ||
    Settings.environment === ENVIRONMENTS.DEVELOPMENT
      ? MAPBOX_TOKENS.DEV
      : MAPBOX_TOKENS.US;

  const handleRef = element => {
    // TODO work out init pattern so don't have to do this
    if (element && !searchInitialized) {
      if (element.children?.length === 0) {
        const geocoder = new MapboxGeocoder({
          accessToken: mapboxToken,
          types: 'country,region,place,postcode',
        });

        geocoder.on('result', location => {
          sortShops(location);
          searchCompleted(location?.result?.text);
        });

        geocoder.addTo(element);
        searchInitialized = true;
      }
    }
  };

  const handleSetPreferredStore = store => {
    setPreferredStore(store.handle);
    closeModal();
  };

  return (
    <Modal isOpen={isOpen} className="StoreInventory__modal" onRequestClose={closeModal}>
      <div className="StoreInventory__modal-body">
        <div className="StoreInventory__heading-container">
          <TertiaryHeading>
            {I18n.localize('pdp.storeInventory.modal.storeInventory')}
          </TertiaryHeading>
        </div>

        <div className="StoreInventory__description-container">
          <Paragraph>{I18n.localize('pdp.storeInventory.modal.selectPreferredStore')}</Paragraph>
        </div>

        <div className="StoreInventory__product-info-container">
          <ProductInfo product={product} variant={variant} />
        </div>

        <div className="StoreInventory__geocoder" ref={handleRef}></div>

        <StoreInfo
          sortedStores={sortedStores}
          storeList={storeList}
          defaultSortedList={defaultSortedList}
          handleSetPreferredStore={handleSetPreferredStore}
          preferredStore={preferredStore}
          storeSelected={storeSelected}
          storeSaved={storeSaved}
        />
      </div>
      <style jsx global>
        {`
          .StoreInventory__heading-container {
            margin-bottom: ${Theme.get('spacing') * 2}px;
          }

          .StoreInventory__description-container {
            border-bottom: 1px solid ${Theme.get('colors-black-20')};
            margin-bottom: 18px;
          }

          .StoreInventory__modal.Modal__content {
            max-width: 632px;
            padding: 24px 24px;
            margin: 0;
            height: 90vh;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }

          .StoreInventory__product-info-container {
            border-bottom: 1px solid ${Theme.get('colors-black-20')};
            margin-bottom: 18px;
            padding-bottom: 18px;
          }

          .StoreInventory__geocoder .mapboxgl-ctrl-geocoder {
            max-width: 100%;
            width: 100%;
          }

          .StoreInventory__geocoder {
            margin-bottom: 30px;
          }

          .StoreInventory__modal-body {
            overflow: hidden;
            display: flex;
            flex-direction: column;
            // need below to handle x axis clipping
            padding: 0 8px;
          }
        `}
      </style>
    </Modal>
  );
};

StoreInventoryModal.propTypes = {
  isOpen: PropTypes.bool,
  closeModal: PropTypes.func.isRequired,
  sortShops: PropTypes.func.isRequired,
  product: PropTypes.instanceOf(ProductModel),
  variant: PropTypes.instanceOf(VariantModel),
  setPreferredStore: PropTypes.func.isRequired,
  sortedStores: PropTypes.arrayOf(
    PropTypes.shape({
      handle: PropTypes.string.isRequired,
    })
  ),
  defaultSortedList: PropTypes.arrayOf(
    PropTypes.shape({
      handle: PropTypes.string.isRequired,
    })
  ),
  storeList: PropTypes.shape({
    handle: PropTypes.string,
    streetAddress: PropTypes.string,
    storeName: PropTypes.string,
    phoneNumber: PropTypes.string,
  }),
  preferredStore: PropTypes.shape({
    handle: PropTypes.string,
  }),
  storeSelected: PropTypes.func.isRequired,
  storeSaved: PropTypes.func.isRequired,
  searchCompleted: PropTypes.func.isRequired,
};

StoreInventoryModal.defaultProps = {
  isOpen: false,
  sortedStores: [],
  preferredStore: {
    handle: null,
  },
};

export default StoreInventoryModal;
